import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isAnyModalOpen } from "@/components/ModalChrome";

export interface Command {
  id: string;
  label: string;
  group?: string;
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteState {
  isOpen: boolean;
  commands: Command[];
}

interface CommandPaletteActions {
  open: () => void;
  close: () => void;
  toggle: () => void;
  register: (command: Command) => void;
  unregister: (id: string) => void;
  registerMany: (commands: Command[]) => void;
  unregisterMany: (ids: string[]) => void;
}

type CommandPaletteContextValue = CommandPaletteState & CommandPaletteActions;

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
  null,
);

interface CommandPaletteProviderProps {
  children: ReactNode;
}

export function CommandPaletteProvider({
  children,
}: CommandPaletteProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const commandsRef = useRef<Map<string, Command>>(new Map());
  const [commandsVersion, setCommandsVersion] = useState(0);
  // A modal owns the screen while it is up: the palette does not open
  // over it — from the shortcut or the toolbar button — and stays closed
  // rather than queueing. Closing is never gated, so ⌘K on an open palette
  // still dismisses it.
  const open = useCallback(() => {
    if (!isAnyModalOpen()) setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(
    () => setIsOpen((prev) => (prev ? false : !isAnyModalOpen())),
    [],
  );

  const register = useCallback((command: Command) => {
    commandsRef.current.set(command.id, command);
    setCommandsVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((id: string) => {
    commandsRef.current.delete(id);
    setCommandsVersion((v) => v + 1);
  }, []);

  const registerMany = useCallback((commands: Command[]) => {
    for (const cmd of commands) {
      commandsRef.current.set(cmd.id, cmd);
    }
    setCommandsVersion((v) => v + 1);
  }, []);

  const unregisterMany = useCallback((ids: string[]) => {
    for (const id of ids) {
      commandsRef.current.delete(id);
    }
    setCommandsVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isMod = navigator.platform.toUpperCase().includes("MAC")
        ? e.metaKey
        : e.ctrlKey;

      if (isMod && e.key === "k") {
        // Always eaten — the browser's own ⌘K must not fire under a modal.
        e.preventDefault();
        toggle();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: commandsVersion tracks map mutations
  const commands = useMemo(
    () => Array.from(commandsRef.current.values()),
    [commandsVersion],
  );

  const value = useMemo<CommandPaletteContextValue>(
    () => ({
      isOpen,
      commands,
      open,
      close,
      toggle,
      register,
      unregister,
      registerMany,
      unregisterMany,
    }),
    [
      isOpen,
      commands,
      open,
      close,
      toggle,
      register,
      unregister,
      registerMany,
      unregisterMany,
    ],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error(
      "useCommandPalette must be used within CommandPaletteProvider",
    );
  }
  return context;
}

export function useRegisterCommands(commands: Command[]) {
  const { registerMany, unregisterMany } = useCommandPalette();

  useEffect(() => {
    if (commands.length === 0) return;
    registerMany(commands);
    const ids = commands.map((c) => c.id);
    return () => unregisterMany(ids);
  }, [commands, registerMany, unregisterMany]);
}
